import React, { useEffect, useState } from 'react'
import { Box, Container, Loader, Center, SimpleGrid } from '@mantine/core'
import { AppBadge, AppInput, AppModal } from '../../app/common/ui'
import { AppButton, AppText, AppStack } from '../../app/common/ui'
import { useParams, useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import { CartridgeSpecDto, AmmoProductDto } from '../../app/models/ammo'
import { ReloaderRecipeDto } from '../../app/models/reloaderRecipe'
import { PagedResult } from '../../app/models/pagedResult'
import { useStore } from '../../app/stores/store'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'

const CartridgeSpecDetail = observer(() => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore } = useStore()
  const [spec, setSpec] = useState<CartridgeSpecDto | null>(null)
  const [products, setProducts] = useState<AmmoProductDto[]>([])
  const [recipes, setRecipes] = useState<ReloaderRecipeDto[]>([])
  const [loading, setLoading] = useState(true)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)

  useEffect(() => {
    Promise.all([
      agent.CartridgeSpecs.details(id),
      agent.AmmoProducts.list({ cartridgeSpecId: id, pageSize: 100 }),
      agent.ReloaderRecipes.list({ cartridgeSpecId: id, pageSize: 20 })
    ])
      .then(([cartridgeSpec, productsResult, recipesResult]: [CartridgeSpecDto, PagedResult<AmmoProductDto>, PagedResult<ReloaderRecipeDto>]) => {
        setSpec(cartridgeSpec)
        setProducts(productsResult.items)
        setRecipes(recipesResult.items)
      })
      .finally(() => setLoading(false))
  }, [id])

  const handleApprove = async () => {
    if (!spec) return
    try {
      await agent.CartridgeSpecs.approve(spec.id)
      toast.success('Cartridge spec approved')
      setSpec((prev) => (prev ? { ...prev, isActive: true } : null))
    } catch (error) {
      toast.error('Failed to approve')
    }
  }

  const handleReject = async () => {
    if (!spec) return
    try {
      await agent.CartridgeSpecs.reject(spec.id, rejectReason)
      toast.success('Cartridge spec rejected')
      setShowRejectModal(false)
      setSpec((prev) => (prev ? { ...prev, isActive: false } : null))
    } catch (error) {
      toast.error('Failed to reject')
    }
  }

  if (loading) {
    return (
      <Center>
        <Loader />
      </Center>
    )
  }

  if (!spec) { return <AppText>Spec not found</AppText> }

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh', paddingTop: '24px', paddingBottom: '24px' }}>
      <Container size="md">
        <AppStack spacing="md">
          {/* Back Link */}
          <AppButton
            variant="subtle"
            onClick={() => history.push('/ammo')}
            sx={{ width: 'fit-content', paddingLeft: 0 }}
          >
            ← Back to Ammo Registry
          </AppButton>

          {/* Header */}
          <Box>
            <AppText
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                marginBottom: '8px'
              }}
            >
              {spec.name}
            </AppText>
            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[spec.shortName, spec.caliber, spec.type].filter(Boolean).map((value) => (
                <AppBadge key={value} variant="outline" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {value}
                </AppBadge>
              ))}
            </Box>
          </Box>

          {/* Admin Panel */}
          {userStore.user?.isAdmin && (
            <Box
              sx={{
                padding: '16px',
                background: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                borderRadius: '6px'
              }}
            >
              <AppText weight={700} size="sm" sx={{ marginBottom: '12px' }}>
                Admin Actions
              </AppText>
              <AppButton.Group>
                <AppButton size="sm" onClick={handleApprove} disabled={spec.isActive ?? true}>
                  Approve
                </AppButton>
                <AppButton size="sm" color="red" onClick={() => setShowRejectModal(true)} disabled={spec.isActive === false}>
                  Reject
                </AppButton>
              </AppButton.Group>
              {spec.isActive === false && (
                <AppText size="xs" color="dimmed" mt="xs">
                  This cartridge spec is inactive and hidden from the public catalog.
                </AppText>
              )}
            </Box>
          )}

          {/* Products */}
          <Box>
            <AppText weight={700} size="lg" sx={{ marginBottom: '12px' }}>
              Loads ({products.length})
            </AppText>
            {products.length === 0
              ? (
              <Box
                sx={{
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <AppText sx={{ color: 'var(--gs-muted)' }}>
                  No loads for this cartridge yet.
                </AppText>
              </Box>
                )
              : (
              <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                {products.map((product) => (
                  <Box
                    key={product.id}
                    onClick={() => history.push(`/ammo/products/${product.id}`)}
                    sx={{
                      padding: '12px',
                      border: '1px solid var(--gs-border)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: 'var(--gs-surface)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#1A3A5C',
                        background: 'var(--gs-surface2)'
                      }
                    }}
                  >
                    <AppText weight={700} size="sm" sx={{ marginBottom: '4px' }}>
                      {product.brand} {product.name}
                    </AppText>
                    <AppText
                      size="xs"
                      sx={{
                        color: 'var(--gs-text)',
                        fontFamily: 'JetBrains Mono, monospace'
                      }}
                    >
                      {product.grainWeight}gr {product.bulletType}
                    </AppText>
                  </Box>
                ))}
              </SimpleGrid>
                )}
          </Box>

          {/* Reloader Recipes */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <AppText weight={700} size="lg">
                Community Recipes ({recipes.length})
              </AppText>
              {recipes.length > 0 && (
                <AppButton size="xs" variant="light" onClick={() => history.push(`/ammo/reloading?cartridgeSpecId=${id}`)}>
                  View All
                </AppButton>
              )}
            </Box>
            {recipes.length === 0
              ? (
              <Box
                sx={{
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <AppText sx={{ color: 'var(--gs-muted)', marginBottom: '12px' }}>
                  No recipes yet for this cartridge.
                </AppText>
                <AppButton size="sm" onClick={() => history.push('/ammo/reloading/new')} color="ember">
                  Post a Recipe
                </AppButton>
              </Box>
                )
              : (
              <SimpleGrid cols={1} spacing="sm">
                {recipes.slice(0, 3).map((recipe) => (
                  <Box
                    key={recipe.id}
                    onClick={() => history.push(`/ammo/reloading/${recipe.id}`)}
                    sx={{
                      padding: '12px',
                      border: '1px solid var(--gs-border)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: 'var(--gs-surface)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#1A3A5C',
                        background: 'var(--gs-surface2)'
                      }
                    }}
                  >
                    <AppText weight={600} size="sm" sx={{ marginBottom: '4px' }}>
                      {recipe.powderBrand} {recipe.powderType}
                    </AppText>
                    <AppText size="xs" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--gs-text)' }}>
                      {recipe.chargeWeightGrMin}-{recipe.chargeWeightGrMax}gr | {recipe.bulletWeightGr}gr {recipe.bulletType}
                    </AppText>
                    <AppText size="xs" color="dimmed" mt={4}>
                      {recipe.validationCount} validations • {recipe.authorDisplayName || recipe.authorUsername}
                    </AppText>
                  </Box>
                ))}
              </SimpleGrid>
                )}
          </Box>

          {/* Compatible Firearms */}
          <Box>
            <AppText weight={700} size="lg" sx={{ marginBottom: '12px' }}>
              Compatible Firearms
            </AppText>
            {spec && spec.compatibleFirearms && spec.compatibleFirearms.length > 0
              ? (
              <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                {spec.compatibleFirearms.map((fw) => (
                  <Box
                    key={fw.id}
                    onClick={() => history.push(`/registry/${fw.id}`)}
                    sx={{
                      padding: '12px',
                      border: '1px solid var(--gs-border)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: 'var(--gs-surface)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#1A3A5C',
                        background: 'var(--gs-surface2)'
                      }
                    }}
                  >
                    <AppText weight={700} size="sm" sx={{ marginBottom: '4px' }}>
                      {fw.make} {fw.model}
                    </AppText>
                    {fw.platform && (
                      <AppText
                        size="xs"
                        sx={{
                          color: 'var(--gs-text)',
                          fontFamily: 'JetBrains Mono, monospace'
                        }}
                      >
                        {fw.platform}
                      </AppText>
                    )}
                  </Box>
                ))}
              </SimpleGrid>
                )
              : (
              <Box
                sx={{
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <AppText sx={{ color: 'var(--gs-muted)' }}>
                  No compatible firearms data available.
                </AppText>
              </Box>
                )}
          </Box>
        </AppStack>
      </Container>

      {/* Reject Modal */}
      <AppModal
        opened={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Cartridge Spec"
      >
        <AppStack spacing="md">
          <AppInput
            label="Reason (optional)"
            placeholder="Why is this being rejected?"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
          />
          <AppButton.Group position="right">
            <AppButton variant="subtle" onClick={() => setShowRejectModal(false)}>
              Cancel
            </AppButton>
            <AppButton color="red" onClick={handleReject}>
              Reject
            </AppButton>
          </AppButton.Group>
        </AppStack>
      </AppModal>
    </Box>
  )
})

export default CartridgeSpecDetail
